import type { ComponentProps } from "react";
import type { SanityRouteDocument } from "@ouster/shared";
import { Hero } from "~/features/hero";
import { sanityEditAttribute } from "~/features/sanity/edit-attribute";
import { adaptSanityHeroToCraft, type CraftHeroData } from "./adapter";
export { adaptSanityHeroToCraft, type CraftHeroData } from "./adapter";
type SanityHeroProps = {
  route?: Pick<SanityRouteDocument, "_id" | "_type" | "hero" | "title" | "hardwareMetadata" | "softwareMetadata"> | null;
  block?: React.ReactNode;
};

/**
 * The Rev8 hero stega-cleans its headline/caption (they are fed to GSAP
 * SplitText and `dangerouslySetInnerHTML`), so the text can't carry a
 * click-to-edit payload. Attach explicit `data-sanity` edit attributes instead,
 * resolved from the route document id/type and the hero array entry's `_key`.
 */
function withRev8EditAttributes(data: CraftHeroData, route: SanityHeroProps["route"]): CraftHeroData {
  const heroEntry = route?.hero?.[0];
  if (data.__typename !== "hero_rev8Hero_BlockType" || heroEntry?._type !== "rev8Hero" || !heroEntry._key) {
    return data;
  }
  const target = {
    id: route?._id,
    type: route?._type,
    key: heroEntry._key
  };
  return {
    ...data,
    headlineEditAttribute: sanityEditAttribute({
      id: target.id,
      type: target.type,
      path: `hero[_key=="${target.key}"].headline`
    }),
    captionEditAttribute: sanityEditAttribute({
      id: target.id,
      type: target.type,
      path: `hero[_key=="${target.key}"].caption`
    })
  };
}

/**
 * Inject route-level product metadata (title/caption/tag/specs) into the
 * The product and sensor hero view contracts use route-level metadata rather
 * than fields in the hero definition.
 */
function withRouteContext(data: CraftHeroData, route: SanityHeroProps["route"]): CraftHeroData {
  const caption = route?.hardwareMetadata?.productCaption ?? route?.softwareMetadata?.productCaption;
  switch (data.__typename) {
    case "hero_productHeroMedia_BlockType":
      return {
        ...data,
        title: {
          text: route?.title,
          size: data.titleSize as "default" | "large" | undefined ?? "default"
        },
        caption
      };
    case "hero_productHeroScrollableVideo_BlockType":
      // Prefer the hero's own `headline`; fall back to the route document
      // title (the original behaviour before the field was added).
      return {
        ...data,
        title: data.headline || route?.title,
        caption
      };
    case "hero_sensorConfiguratorHero_BlockType":
      return {
        ...data,
        title: route?.title,
        caption,
        tag: route?.hardwareMetadata?.productGeneration?.title,
        highlights: route?.hardwareMetadata?.productHighlights ?? []
      };
    default:
      return data;
  }
}

/**
 * Renders a route document's hero (its first `hero[]` entry) by adapting the
 * Sanity hero into view-contract data and dispatching through the existing
 * `Hero` component. Renders nothing when there is no mappable hero.
 */
export function SanityHero({
  route,
  block
}: SanityHeroProps) {
  const adapted = adaptSanityHeroToCraft(route?.hero?.[0]);
  if (!adapted) {
    return null;
  }
  const heroProps = withRev8EditAttributes(withRouteContext(adapted, route), route) as unknown as ComponentProps<typeof Hero>;
  return <Hero {...heroProps} block={block} />;
}